import styled from "@emotion/styled";
import { forwardRef, useImperativeHandle } from "react";
import { i18n, PersonType, useForm } from "src/common";
import { Text, Card, CheckboxInput, InputBirthDate, InputCity, InputOIB, InputPhoneNumber, InputStreet, InputText, Validation, RadioButtonGroup } from "src/components";
import { FieldConfig, Form, FormRef } from "./form";

export type InsuredPersonFormItemType = 'type' | 'sameAddressAsProperty' | 'firstName' | 'lastName' | 'companyName' | 'birthDate' | 'oib' | 'city' | 'street' | 'houseNumber' | 'zipCode' | 'email' | 'phoneNumber';
export const allInsuredPersonDataFormItems: Array<InsuredPersonFormItemType> = ['type', 'sameAddressAsProperty', 'firstName', 'lastName', 'companyName', 'birthDate', 'oib', 'city', 'street', 'houseNumber', 'zipCode', 'email', 'phoneNumber',];

export interface InsuredPersonsFormValues {
  type?: PersonType,
  sameAddressAsProperty: boolean;
  firstName: string;
  lastName: string;
  companyName: string;
  birthDate: string;
  oib?: string;
  city?: string;
  cityKompareId: string;
  street?: string;
  streetKompareId: string;
  houseNumber?: string;
  zipCode?: number;
  email?: string;
  phoneNumber?: string;
}

interface InsuredPersonsFormProps {
  insuredPersonData?: InsuredPersonsFormValues;
  setSameAddressAsProperty: (same: boolean) => void;
  setInsuredType: (person: PersonType) => void;
  formItems?: Array<InsuredPersonFormItemType>;
  onSubmit?: (values: InsuredPersonsFormValues) => void;
}

export const InsuredPersonsFormItem = styled.div({
  marginBottom: '24px'
});

export const InsuredPersonsForm = forwardRef<FormRef, InsuredPersonsFormProps>(({
  insuredPersonData,
  setSameAddressAsProperty,
  setInsuredType,
  formItems = allInsuredPersonDataFormItems,
  onSubmit
}, ref) => {
  const { formRef, submitForm, getValues, validateForm } = useForm();

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        submitForm();
      }
    },
    getValues: () => getValues?.() ?? {},
    validateForm: () => validateForm?.() ?? false,
    setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
      formRef.current?.setFieldValue?.(fieldName, value, validation);
    },
  }));

  const handleSubmit = (values: InsuredPersonsFormValues) => {
    if (onSubmit) onSubmit(values);
  };

  const fields: FieldConfig<InsuredPersonsFormValues>[] = [
    {
      name: 'type',
      component: RadioButtonGroup,
      options: Object.entries(PersonType).map(([_value, label]) => ({ label: i18n.t(`components.forms.contractor.${_value}`), value: label })),
      componentProps: {
        horizontal: true
      },
      onChange: setInsuredType
    },
    {
      name: 'sameAddressAsProperty',
      component: CheckboxInput,
      componentProps: {
        left: true,
        labelComponent: <Text type="base">{i18n.t('components.forms.insuredPerson.sameAddressAsProperty')}</Text>,
      },
      onChange: setSameAddressAsProperty
    },
    {
      name: 'firstName',
      label: i18n.t('components.forms.contractor.firstName'),
      component: InputText,
      componentProps: {
        regex: /[^-a-zA-ZčćžšđČĆŽŠĐ]/gu
      },
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.firstNameRequired')
        }
      ]
    },
    {
      name: 'lastName',
      label: i18n.t('components.forms.contractor.lastName'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.lastNameRequired')
        }
      ]
    },
    {
      name: 'companyName',
      label: i18n.t('components.forms.contractor.companyName'),
      component: InputText,
      validations: [
        {
          type: 'required',
          message: i18n.t('components.forms.contractor.lastNameRequired')
        }
      ]
    },
    {
      name: "birthDate",
      label: i18n.t("components.forms.contractor.birthDate"),
      component: InputBirthDate,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.birthDateRequired") },
        { type: "validOlderThan18", message: i18n.t("components.forms.contractor.birthDateOlderThan18") },
      ],
    },
    {
      name: "oib",
      label: i18n.t("components.forms.contractor.oib"),
      component: InputOIB,
      validations: [
        { type: "required", message: i18n.t("common.validation.oibRequired"), params: { optional: true } },
        { type: "validOIB", message: i18n.t("common.validation.oibValid"), params: { optional: true } }
      ]
    },
    {
      name: "city",
      label: i18n.t("components.forms.contractor.city"),
      component: InputCity,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.cityRequired") },
      ] as Validation[],
      formatValue: (values: any) =>
        (values.city || values.zipCode) &&
        `${values.city}, ${values.zipCode}`,
      disabledIf: (values: InsuredPersonsFormValues) => values.sameAddressAsProperty
    },
    {
      name: "street",
      label: i18n.t("components.forms.contractor.street"),
      component: InputStreet,
      componentProps: {
        cityKompareId: 'cityKompareId',
      },
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.streetRequired") },
      ] as Validation[],
      disabledIf: (values: InsuredPersonsFormValues) => values.sameAddressAsProperty
    },
    {
      name: "houseNumber",
      label: i18n.t("components.forms.contractor.houseNumber"),
      component: InputText,
      validations: [
        { type: "required", message: i18n.t("components.forms.contractor.houseNumberRequired") },
      ] as Validation[],
      disabledIf: (values: InsuredPersonsFormValues) => values.sameAddressAsProperty
    },
    {
      name: 'email',
      label: i18n.t('components.forms.contractor.email'),
      component: InputText,
      componentProps: {
        type: 'email',
        maxLength: 50,
      },
      validations: [
        { type: 'required', message: i18n.t("components.forms.contractor.emailRequired") },
        { type: 'validMail', message: i18n.t("components.forms.contractor.emailValid") },
      ] as Validation[],
    },
    {
      name: 'phoneNumber',
      label: i18n.t('components.forms.customer.phone'),
      component: InputPhoneNumber,
      formatValue: (values: InsuredPersonsFormValues & { countryCode?: string; cleanPhoneNumber?: string }) =>
        values.phoneNumber ?? '',
      validations: [
        { type: 'required', message: i18n.t('common.validation.validPhoneNumber') },
        {
          type: 'validPhoneNumber',
          message: i18n.t('common.validation.validPhoneNumber'),
          params: { regionCode: 'countryCode' },
        },
      ] as Validation[],
    }
  ].filter((field) => formItems.includes(field.name as InsuredPersonFormItemType));

  const initialValues = { ...insuredPersonData, ...getValues(), type: insuredPersonData?.type };

  return (
    <Card style={{ paddingBottom: 24 }}  title={<Text type="medium" bold>{i18n.t('components.forms.insuredPerson.title')}</Text>}>
      <Form ref={formRef} fields={fields} onSubmit={handleSubmit} initialValues={initialValues} />
    </Card>
  );
});
